import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, withProps } from 'recompose';
import { withGoogleMap, GoogleMap } from 'react-google-maps';

import {
  selectInvestorResults,
  selectSubjectLocation,
  selectSubject,
  selectSubjectProperty,
  buildInvestorPins,
  buildSubjectPin,
} from 'data/investors';

import InvestorMarker from './InvestorMarker';
import css from './style.scss';

// Map pane for the Investor Search surface — Figma node 291:8804 (the "map"
// frame filling the left column below the search + filter section).
//
// Modelled on app/components/Map but deliberately a fraction of it: that
// component carries drawing tools, parcel click-through, street-view panoramas,
// analytics overlays, property info windows and coupling to the search slice —
// none of which this surface needs. What is kept is the setup that matters:
// the same react-google-maps composition, the same window.UserLocation centre,
// and the map-type control in the bottom-left corner as the design shows.
//
// It renders empty until a search runs: the PRD is explicit that the map holds
// no investor data before then. After a search, each comparable property
// becomes one pin, themed by its owning investor's first classification — see
// ./investorPins.

const DEFAULT_CENTER = { lat: 37.0902, lng: -95.7129 };
const DEFAULT_ZOOM = 5;

const getInitialView = () => {
  const { latitude, longitude, zoom } = window.UserLocation || {};

  return {
    center: latitude && longitude ? { lat: latitude, lng: longitude } : DEFAULT_CENTER,
    zoom: zoom || DEFAULT_ZOOM,
  };
};

// Google's own defaults already give the fullscreen, zoom and street-view
// controls in the design; only the map-type toggle needs repositioning.
const getMapOptions = () =>
  !window.google
    ? undefined
    : { mapTypeControlOptions: { position: window.google.maps.ControlPosition.LEFT_BOTTOM } };

// Once a search has run, centre on the subject property the service geocoded;
// before that, fall back to the user's own location.
const getSubjectPosition = (subjectLocation) => {
  const lat = Number(subjectLocation && subjectLocation.latitude);
  const lng = Number(subjectLocation && subjectLocation.longitude);

  return Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)
    ? { lat, lng }
    : null;
};

const getSearchView = (subjectLocation) => {
  const at = getSubjectPosition(subjectLocation);

  return at ? { center: at, zoom: 13 } : getInitialView();
};

// Pixels the map is nudged after centring, so the selected pin sits below the
// middle: the card opens upwards, and an exactly centred pin would leave it only
// half the viewport to fit into. Negative y moves the content down, which is
// what puts the pin lower on screen.
const SELECT_CLEARANCE = 140;

// Edge padding for the results fit, so pins do not land under the map's own
// controls or get clipped at the border.
const FIT_PADDING = { top: 48, right: 48, bottom: 48, left: 48 };

// A single result would otherwise fit to the maximum zoom, which is useless
// context; and a very tight cluster should not zoom past street level.
const MAX_RESULT_ZOOM = 17;

const InvestorMap = ({ pins, subjectLocation, subjectProperty }) => {
  const [activeKey, setActiveKey] = useState(null);
  const mapRef = useRef(null);
  // Defaults only — the map is deliberately uncontrolled. react-google-maps
  // compares props by reference, so a `center` object rebuilt on each render
  // would call setCenter on every state change and snap the map back, undoing
  // the pan below.
  const initialView = useRef(getSearchView(subjectLocation));

  // ATAI-316: the subject gets its own star pin unless an investor's pin
  // already is the subject (that one draws the star itself, and stays
  // clickable for its investor card). Placed where the search geocoded it,
  // else where the subject property lookup did.
  const standaloneSubject =
    getSubjectPosition(subjectLocation) ||
    getSubjectPosition(subjectProperty && subjectProperty.property);

  // Once the subject lookup names an owning investor, the star opens that
  // investor's card on click, like any other pin; otherwise it stays plain.
  // Kept out of `pins` so its arrival doesn't re-run the results fit.
  // Keyed on the coordinates, not the position object, which is rebuilt every
  // render and would hand the marker a fresh pin each time.
  const subjectLat = standaloneSubject ? standaloneSubject.lat : null;
  const subjectLng = standaloneSubject ? standaloneSubject.lng : null;
  const subjectPin = useMemo(
    () =>
      buildSubjectPin(
        subjectProperty,
        subjectLat === null ? null : { lat: subjectLat, lng: subjectLng }
      ),
    [subjectProperty, subjectLat, subjectLng]
  );

  // Fit the map to the results when a search lands, so the pins are spread out
  // and individually distinguishable rather than piled up at a fixed zoom.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    setActiveKey(null);

    const maps = window.google && window.google.maps;

    if (pins.length > 1 && maps && map.fitBounds) {
      const bounds = new maps.LatLngBounds();

      pins.forEach((pin) => bounds.extend(pin.position));

      // Keep the subject in frame: it is the anchor the results are relative to.
      const subject = getSubjectPosition(subjectLocation);
      if (subject) bounds.extend(subject);

      map.fitBounds(bounds, FIT_PADDING);

      // fitBounds alone can overshoot on a tight cluster.
      if (map.getZoom && map.setZoom && map.getZoom() > MAX_RESULT_ZOOM) {
        map.setZoom(MAX_RESULT_ZOOM);
      }

      return;
    }

    // One result, or no Maps API to build bounds with: centre on what we have.
    const single = pins.length === 1 ? pins[0].position : null;
    const view = getSearchView(subjectLocation);

    if (!single && !subjectLocation) return;
    if (map.panTo) map.panTo(single || view.center);
    if (map.setZoom) map.setZoom(single ? MAX_RESULT_ZOOM : view.zoom);
  }, [pins, subjectLocation]);

  // Re-centre on the selected pin so its card clears the map's edges: the map
  // clips its own overlays, so panning is what makes a card near the border
  // fully visible. Clicking the open pin again closes it, without panning.
  const handleSelect = (pin) => {
    if (pin.key === activeKey) {
      setActiveKey(null);

      return;
    }

    setActiveKey(pin.key);

    const map = mapRef.current;
    if (!map || !map.panTo) return;

    map.panTo(pin.position);
    if (map.panBy) map.panBy(0, -SELECT_CLEARANCE);
  };

  return (
    <GoogleMap
      ref={mapRef}
      defaultZoom={initialView.current.zoom}
      defaultCenter={initialView.current.center}
      defaultMapTypeId="roadmap"
      options={getMapOptions()}
      onClick={() => setActiveKey(null)}
    >
      {subjectPin && (
        <InvestorMarker
          pin={subjectPin}
          active={subjectPin.key === activeKey}
          onSelect={handleSelect}
          onClose={() => setActiveKey(null)}
        />
      )}
      {pins.map((pin) => (
        <InvestorMarker
          key={pin.key}
          pin={pin}
          active={pin.key === activeKey}
          onSelect={handleSelect}
          onClose={() => setActiveKey(null)}
        />
      ))}
    </GoogleMap>
  );
};

InvestorMap.propTypes = {
  pins: PropTypes.array,
};

InvestorMap.defaultProps = {
  pins: [],
};

const GoogleInvestorMap = compose(
  withProps({
    loadingElement: <div className={css.fill} />,
    containerElement: <div className={css.fill} />,
    mapElement: <div className={css.fill} />,
  }),
  withGoogleMap
)(InvestorMap);

const MapPane = ({ investors, subjectLocation, subject, subjectProperty }) => {
  // The searched property's id, so buildInvestorPins can flag its marker as the
  // subject (star) pin. A primitive dep keeps the memo from recomputing on
  // unrelated subject-object changes.
  const subjectPropertyId = subject ? subject.propertyId : null;

  // Memoised so the pin array keeps its identity between renders: the fit
  // effect keys off it, and a fresh array each render would refit endlessly.
  const pins = useMemo(
    () => buildInvestorPins(investors, subjectPropertyId),
    [investors, subjectPropertyId]
  );

  return (
    <div className={css.container}>
      <GoogleInvestorMap
        pins={pins}
        subjectLocation={subjectLocation}
        subjectProperty={subjectProperty}
      />
    </div>
  );
};

MapPane.propTypes = {
  investors: PropTypes.array,
};

MapPane.defaultProps = {
  investors: [],
};

// Connected here rather than threaded through the page, so wiring the map does
// not disturb the surface's layout component.
export default connect((state) => ({
  investors: selectInvestorResults(state),
  subjectLocation: selectSubjectLocation(state),
  subject: selectSubject(state),
  subjectProperty: selectSubjectProperty(state),
}))(MapPane);
